import React, { useState, Fragment, useEffect, useCallback } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import Form from "@rjsf/core";
import validator from "@rjsf/validator-ajv8";
import { jwtDecode } from "jwt-decode";
import { MENU_SLUG } from "../../constants/constants";
import axios from "../../api/axios";
import LoginLeftBg from "../../components/loginLeftBg";
import LoadingButton from "../../components/loadingButton";
import ErrorMessage from "../../components/errorMessage";
import PasswordInput from "../../components/passwordInput";
import RightArrowIcon from "../../icons/rightArrow";
import { useAuth } from "../../hooks/useAuth";
import useMediaQuery from "../../hooks/useMediaQuery";
import LogoImg from "../../assets/images/logo.png";
import styles from "./style.module.css";
import formStyles from "../../components/form/style.module.css";

const CustomTextWidget = function (props) {
  const { label, options } = props;
  if (options.link) {
    return (
      <div className={styles.form_link_wrapper}>
        <Link to={options.link}>{label}</Link>
      </div>
    );
  }
  return <div className={styles.form_text}>{label}</div>;
};

const schema = {
  type: "object",
  required: ["email", "password"],
  properties: {
    email: {
      title: "Email Address",
      type: "string",
      format: "email",
    },
    password: {
      title: "Password",
      type: "string",
    },
    remember_me: {
      type: "boolean",
      title: "Remember me",
      default: false,
    },
    forgot_password: {
      type: "string",
      title: "Forgot Password?",
    },
  },
};

const uiSchema = {
  "ui:classNames": `${formStyles.formInner} ${styles.form_inner}`,
  "ui:rootFieldId": "add_track_day",
  email: {
    "ui:classNames": `${formStyles.form_field} ${formStyles.col_12}`,
    "ui:placeholder": "youremail@gmail.com",
  },
  password: {
    "ui:widget": PasswordInput,
    "ui:classNames": `${formStyles.form_field} ${formStyles.col_12}`,
    "ui:placeholder": "••••••••",
  },
  remember_me: {
    "ui:classNames": `${formStyles.form_checkbox} ${formStyles.col_6}`,
  },
  forgot_password: {
    "ui:widget": CustomTextWidget,
    "ui:classNames": `${styles.form_link} ${formStyles.col_6}`,
    "ui:options": {
      label: false,
      link: `/${MENU_SLUG.forgot_password}`,
    },
  },
  "ui:submitButtonOptions": {
    props: {
      className: formStyles.form_btn,
    },
    submitText: (
      <Fragment>
        <span>Sign In</span> <RightArrowIcon />
      </Fragment>
    ),
  },
};

const widgets = {
  forgot_password: CustomTextWidget,
  password: PasswordInput,
};

const Login = () => {
  const isMobile = useMediaQuery("(max-width: 767px)");
  let navigate = useNavigate();
  let location = useLocation();

  const { setAuth, persist, setPersist } = useAuth();

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({});
  const [extraErrors, setExtraErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState("");

  const from =
    (location.state && location.state.from.pathname) || `/${MENU_SLUG.my_cars}`;

  const onFormSubmit = (formData) => {
    localStorage.setItem("remember_me", formData.remember_me);
    document.cookie = `remember_me=${formData.remember_me};`;
    setFormData(formData);
    let tempFormData = { ...formData };
    delete tempFormData.remember_me;
    handleLogin(tempFormData);
  };

  const handleLogin = useCallback(
    async (data) => {
      setErrorMessage("");
      try {
        setLoading(true);
        let url = `auth/login`;
        let options = {
          method: "POST",
          url,
          data,
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: true,
        };
        await axios(options)
          .then((response) => {
            if (response.data.status === 1) {
              let decoded = jwtDecode(response.data.accessToken);
              const token = response.data.accessToken;
              setAuth({
                id: decoded.id,
                currentCar: decoded.currentCar,
                monitorBy: decoded.monitorBy,
                token: token,
                multipleCars: decoded.multipleCars,
                ndaAccepted: decoded.ndaAccepted,
                paymentWindow: decoded.paymentWindow,
                trialDays: decoded.trialDays,
                membershipAmount: decoded.membershipAmount,
                membership: decoded.membership,
              });
              localStorage.setItem("persist", true);
              localStorage.setItem("tk", token);
              localStorage.setItem("rtkn", response.data.refreshToken);
              setPersist(true);
              setExtraErrors({});
              navigate(from, { replace: true });
            } else {
              let tempErr = { ...extraErrors };
              tempErr.email = { __errors: [response.data.message.email] };
              tempErr.password = { __errors: [response.data.message.password] };
              setExtraErrors(tempErr);
            }
            setLoading(false);
          })
          .catch((err) => {
            if (err.response) {
              setLoading(false);
              setErrorMessage(err.response.data.message);
              // console.error('err.res', err.response.data);
            }
          });
      } catch (error) {
        setLoading(false);
        // console.error('error', error);
      }
    }, // eslint-disable-next-line
    [extraErrors]
  );

  useEffect(() => {
    localStorage.setItem("persist", persist);
  }, [persist]);

  return (
    <div className={styles.main}>
      {isMobile && (
        <div className={styles.main_logo}>
          <img src={LogoImg} alt="Logo" />
        </div>
      )}
      <div className={styles.main_container}>
        {!isMobile && (
          <div className={styles.main_left}>
            <LoginLeftBg />
          </div>
        )}
        <div className={styles.main_right}>
          <div className={styles.form_container}>
            <div className={styles.form_outer}>
              <div className={styles.form_title}>Sign In to Dashbook</div>
              <Form
                className={`${formStyles.form}`}
                schema={schema}
                uiSchema={uiSchema}
                widgets={widgets}
                formData={formData}
                validator={validator}
                showErrorList={false}
                omitExtraData={true}
                liveValidate={false}
                extraErrors={extraErrors}
                onSubmit={({ formData }) => {
                  onFormSubmit(formData);
                }}
              >
                <div className={styles.form_btn_container}>
                  <LoadingButton
                    type="submit"
                    loading={loading}
                    title={"Sign In"}
                    icon={<RightArrowIcon />}
                  />
                </div>
              </Form>
              <div className={styles.form_footer}>
                Don't have an account?{" "}
                <Link to={`/${MENU_SLUG.sign_up}`}>Sign Up</Link>
              </div>
              {errorMessage && <ErrorMessage message={errorMessage} mt />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
